import React, { useState, useCallback } from 'react';
import { Star } from 'lucide-react';
import { useReveal, useInView } from '../hooks/useInView';

// Photo pool. Only 5 assets exist (Testimonial Photo 1..5) so we cycle them
// across the 10 quotes — index chosen for visual variety between rows.
const PHOTOS = [
  '/tesimonial/Testimonial Photo 1.png',
  '/tesimonial/Testimonial Photo 2.png',
  '/tesimonial/Testimonial Photo 3.png',
  '/tesimonial/Testimonial Photo 4.png',
  '/tesimonial/Testimonial Photo 5.png',
];

// Row 1 — drifts to the RIGHT (marquee-track-reverse). Balanced: 2 paired
// image + 3 text-only cards, so this row's total track width matches Row 2.
// Equal widths mean the two rows advance at visually identical speed even
// though CSS runs each animation for the same duration.
const ROW_ONE = [
  {
    name: 'Aisha R.',
    quote:
      'The process felt clear from the beginning. I completed the assessment, understood the next steps, and was connected to a clinic without having to compare many different providers on my own. The doctor listened to my concerns and explained the available options in a way that was easy to understand.',
    photo: PHOTOS[0],
  },
  {
    name: 'Daniel T.',
    quote:
      'I appreciated that the programme started with a proper doctor consultation instead of immediately pushing a product. The clinic team reviewed my health background carefully, answered my questions, and helped me understand what would be suitable for me.',
  },
  {
    name: 'Mei Ling C.',
    quote:
      'My schedule is usually very busy, so having the assessment, clinic matching and booking process organised in one place was helpful. I found a nearby clinic and knew what to expect before attending my consultation.',
    photo: PHOTOS[1],
  },
  {
    name: 'Farah N.',
    quote:
      'What gave me confidence was knowing that the treatment decision would be made by a licensed doctor. The information was straightforward, and I never felt pressured. I was able to take time to understand the programme before deciding on my next step.',
  },
  {
    name: 'Jason L.',
    quote:
      'I had been reading a lot of conflicting information online. MyDr.AI helped simplify the journey and directed me to a clinic where I could discuss my goals properly with a doctor. The consultation gave me a much clearer understanding of the available approach.',
  },
];

// Row 2 — drifts to the LEFT (marquee-track). Same paired/text-only rhythm.
const ROW_TWO = [
  {
    name: 'Priya S.',
    quote:
      'The experience was convenient and professional. I completed the initial questions online, received clear instructions, and attended a clinic close to home. The doctor explained the benefits, possible side effects and follow-up expectations before anything was decided.',
  },
  {
    name: 'Hafiz M.',
    quote:
      'I liked that the focus was not only on weight, but also on my overall health and daily habits. The doctor asked about my lifestyle, medical history and goals before discussing a personalised plan. It felt more responsible than trying random slimming solutions.',
    photo: PHOTOS[3],
  },
  {
    name: 'Joanne K.',
    quote:
      'The platform made it easier for me to take the first step. Instead of feeling embarrassed or unsure about where to go, I could begin privately online and then speak with a doctor at a partner clinic. The whole process felt respectful and organised.',
  },
  {
    name: 'Marcus W.',
    quote:
      'Booking was simple, and the clinic location was convenient for me. I also appreciated receiving clear reminders and knowing that I could follow up with the clinic if I had questions. The structured support helped me feel more accountable to my health goals.',
    photo: PHOTOS[4],
  },
  {
    name: 'Nurul A.',
    quote:
      'I was looking for a trustworthy starting point rather than another quick-fix promise. MyDr.AI connected me to a doctor-guided pathway and gave me enough information to make an informed decision. The experience felt calm, transparent and focused on safety.',
  },
];

function StarRow() {
  return (
    <div className="flex items-center gap-0.5 text-brand-500" aria-label="5 out of 5 stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
      ))}
    </div>
  );
}

// Text-only card body. Name + stars header row, then the quote. Rendered
// on its own when a testimonial has no photo, or paired with a photo card
// when one is provided.
function QuoteCard({ t }) {
  return (
    <div className="shrink-0 w-[240px] sm:w-[360px] rounded-2xl bg-brand-50/60 border border-brand-100 p-4 sm:p-6 flex flex-col gap-2.5 sm:gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="font-bold text-ink-900 leading-tight text-base sm:text-lg">{t.name}</div>
        <StarRow />
      </div>
      <p className="text-[13px] sm:text-sm text-ink-800 leading-relaxed whitespace-normal italic">
        &ldquo;{t.quote}&rdquo;
      </p>
    </div>
  );
}

// One testimonial in the marquee. If `photo` is present we render a paired
// unit: image card on the LEFT, quote card on the RIGHT (per client mockup
// 2026-07-31). If no photo, just the quote card.
function TestimonialCard({ t }) {
  if (!t.photo) return <QuoteCard t={t} />;
  return (
    <div className="shrink-0 flex gap-3 sm:gap-4 items-stretch">
      <div className="shrink-0 w-[160px] sm:w-[260px] aspect-[4/3] rounded-2xl overflow-hidden bg-brand-50 border border-ink-100 shadow-soft">
        <img
          src={t.photo}
          alt=""
          aria-hidden="true"
          loading="lazy"
          draggable="false"
          className="ken-burns w-full h-full object-cover"
        />
      </div>
      <QuoteCard t={t} />
    </div>
  );
}

// One marquee row. `direction` controls which class variant is applied so
// the two rows drift in opposite directions. Press-and-hold (touch or mouse)
// pauses the drift; releasing / leaving resumes it — matches Medvi's calmer
// pacing where users can dwell on a card to read it.
function TestimonialRow({ items, direction = 'left', mobileSec = 70, desktopSec = 55 }) {
  const [paused, setPaused] = useState(false);
  const pause  = useCallback(() => setPaused(true),  []);
  const resume = useCallback(() => setPaused(false), []);
  const trackClass = direction === 'right' ? 'marquee-track-reverse' : 'marquee-track';
  return (
    <div
      className="relative overflow-hidden"
      onPointerDown={pause}
      onPointerUp={resume}
      onPointerLeave={resume}
      onPointerCancel={resume}
    >
      {/* Edge fades so the loop seam is imperceptible */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-ivory to-transparent z-10" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-ivory to-transparent z-10" />
      <div
        className={`${trackClass} ${paused ? 'marquee-paused' : ''} flex gap-5 sm:gap-6 py-3`}
        style={{
          '--marquee-mobile': `${mobileSec}s`,
          '--marquee-desktop': `${desktopSec}s`,
        }}
      >
        {[0, 1].map((dup) => (
          <div key={dup} aria-hidden={dup === 1 ? 'true' : 'false'} className="flex shrink-0 gap-5 sm:gap-6">
            {items.map((t, i) => (
              <TestimonialCard key={`${dup}-${i}`} t={t} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Testimonials() {
  const head = useReveal();
  // One-shot: once the marquee rows enter the viewport, add the class that
  // starts the Ken Burns animation on the photos. Before that, the animation
  // is paused (see .ken-burns in index.css), so we don't do transform work
  // for photos that aren't on screen yet.
  const [rowsRef, rowsInView] = useInView({ threshold: 0.05 });
  return (
    <section className="bg-ivory">
      <div className="container-x pt-12 sm:pt-16 pb-6 sm:pb-8">
        <div ref={head.ref} className={`${head.className} max-w-2xl`}>
          <span className="eyebrow">Patient EXPERIENCES</span>
          <h2 className="h-section mt-3">Real people. Guided journeys. Verified clinics.</h2>
          <p className="lead mt-3">
            The experiences below are shared by programme participants who completed an assessment, attended a consultation at a licensed Malaysian clinic, and chose to share their journey.
          </p>
        </div>
      </div>

      {/* Full-bleed marquee rows — sit outside the container so the ticker
          runs edge-to-edge on wide screens. `ken-burns-active` starts the
          photo zoom animation only after the block enters the viewport. */}
      <div
        ref={rowsRef}
        className={`space-y-5 sm:space-y-6 ${rowsInView ? 'ken-burns-active' : ''}`}
      >
        <TestimonialRow items={ROW_ONE} direction="right" />
        <TestimonialRow items={ROW_TWO} direction="left" />
      </div>

      {/* Disclaimer sits one row-gap below the second marquee row (same
          space-y-5/6 rhythm) and carries the section's bottom padding. */}
      <div className="container-x mt-4 sm:mt-5 pb-12 sm:pb-16">
        <p className="text-xs text-ink-500 max-w-2xl mx-auto text-center">
          Individual experiences and outcomes vary. Testimonials reflect personal experiences and do not guarantee similar results. Treatment suitability is subject to assessment by a licensed healthcare professional.
        </p>
      </div>
    </section>
  );
}
